import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { LocalizationSettings } from './localizationApi';
import { useAuthStore } from '../stores/authStore';

// ── DTOs ──────────────────────────────────────────────────────────────────────

export interface UserPreferences {
  preferredLanguage: string | null;
  preferredTimezone: string | null;
  preferredDateFormat: string | null;
}

export interface UserPreferencesResponse {
  preferences: UserPreferences;
  resolved: LocalizationSettings;
}

/** Mirrors Beetegre.Shared.DTOs.Profile.UserProfileResponse */
export interface UserProfileResponse {
  id: string;
  email: string;
  fullName: string;
  preferredLanguage: string | null;
  preferredTimezone: string | null;
  preferredDateFormat: string | null;
  avatarUrl: string | null;
  avatarMaxBytes: number;
}

/** Mirrors Beetegre.Shared.DTOs.Profile.UpdateProfileRequest */
export interface UpdateProfileRequest {
  fullName: string;
  preferredLanguage: string | null;
  preferredTimezone: string | null;
  preferredDateFormat: string | null;
}

/** Mirrors Beetegre.Shared.DTOs.Profile.ChangeMyPasswordRequest */
export interface ChangeMyPasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

/** Mirrors Beetegre.Shared.DTOs.Profile.AvatarUploadResponse */
export interface AvatarUploadResponse {
  avatarUrl: string;
}

// ── Query-key factory ─────────────────────────────────────────────────────────

export const meKeys = {
  preferences: ['me', 'preferences'] as const,
  profile: ['me', 'profile'] as const,
};

// ── Raw API calls ─────────────────────────────────────────────────────────────

export const meApi = {
  getPreferences: (signal?: AbortSignal): Promise<UserPreferencesResponse> =>
    api.get<UserPreferencesResponse>('/api/v1/me/preferences', { signal }).then((r) => r.data),

  updatePreferences: (body: UserPreferences): Promise<UserPreferencesResponse> =>
    api.put<UserPreferencesResponse>('/api/v1/me/preferences', body).then((r) => r.data),

  getProfile: (signal?: AbortSignal): Promise<UserProfileResponse> =>
    api.get<UserProfileResponse>('/api/v1/me/profile', { signal }).then((r) => r.data),

  updateProfile: (body: UpdateProfileRequest): Promise<UserProfileResponse> =>
    api.put<UserProfileResponse>('/api/v1/me/profile', body).then((r) => r.data),

  changePassword: (body: ChangeMyPasswordRequest): Promise<void> =>
    api.post('/api/v1/me/password', body).then(() => undefined),

  uploadAvatar: (file: File): Promise<AvatarUploadResponse> => {
    const form = new FormData();
    form.append('avatar', file);
    return api
      .post<AvatarUploadResponse>('/api/v1/me/avatar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
};

// ── TanStack Query hooks ──────────────────────────────────────────────────────

export function useUserPreferences() {
  return useQuery<UserPreferencesResponse>({
    queryKey: meKeys.preferences,
    queryFn: ({ signal }) => meApi.getPreferences(signal),
    staleTime: 60_000,
  });
}

/**
 * Updates the user's localization overrides. On success, the authStore's
 * effective localizationSettings is patched with the resolved (tenant +
 * override) values so the entire UI reformats immediately without a full
 * /auth/me round-trip.
 */
export function useUpdateUserPreferences() {
  const qc = useQueryClient();
  const setLocalizationSettings = useAuthStore((s) => s.setLocalizationSettings);
  return useMutation<UserPreferencesResponse, unknown, UserPreferences>({
    mutationFn: (body) => meApi.updatePreferences(body),
    onSuccess: (data) => {
      setLocalizationSettings(data.resolved);
      qc.invalidateQueries({ queryKey: meKeys.preferences });
    },
    meta: { errorToast: true },
  });
}

export function useProfile() {
  return useQuery<UserProfileResponse>({
    queryKey: meKeys.profile,
    queryFn: ({ signal }) => meApi.getProfile(signal),
    staleTime: 60_000,
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation<UserProfileResponse, unknown, UpdateProfileRequest>({
    mutationFn: (body) => meApi.updateProfile(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: meKeys.profile });
    },
    // ProfilePage renders the rejection inline (mapBackendErrors).
    meta: { errorInline: true },
  });
}

export function useChangePassword() {
  return useMutation<void, unknown, ChangeMyPasswordRequest>({
    mutationFn: (body) => meApi.changePassword(body),
    // ProfilePage renders the rejection inline (mapBackendErrors).
    meta: { errorInline: true },
  });
}

export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation<AvatarUploadResponse, unknown, File>({
    mutationFn: (file) => meApi.uploadAvatar(file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: meKeys.profile });
    },
    meta: { errorToast: true },
  });
}
